import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Clock, Calendar, AlertCircle, Info } from 'lucide-react';
import ErrorText from "../../components/errorText";
import { TimeBasedTriggerBlockProps, TimeOffsetConfig, TimeOffsetErrors } from '../../types/automation';
import logger, { Logger } from '../../utils/logger';
import './TimeBasedTriggerBlock.css';

const TimeBasedTriggerBlock: React.FC<TimeBasedTriggerBlockProps> = ({
  eventType,
  timeOffsetConfig,
  onChange,
  taskData,
  automationData,
  onTaskTypeChange,
  isVisible,
  isLoading = false,
  errors,
  onValidationChange
}) => {
  // Local state for validation and UI
  const [localErrors, setLocalErrors] = useState<TimeOffsetErrors>({});
  const [isDirty, setIsDirty] = useState(false);

  logger.log('[TimeBasedTriggerBlock] Rendered with props:', {
    eventType,
    timeOffsetConfig,
    isVisible,
    taskDataLength: taskData?.length,
    targetValues: automationData?.targetValues
  });

  // Validation logic
  const validateConfig = useCallback((config: TimeOffsetConfig): TimeOffsetErrors => {
    logger.log('[TimeBasedTriggerBlock] Validating config:', config);
    
    const newErrors: TimeOffsetErrors = {};
    
    // Validate amount
    if (!config.amount || config.amount < 1 || config.amount > 365) {
      newErrors.amount = "Days must be between 1 and 365";
    }
    
    // Validate operation
    if (!config.operation) {
      newErrors.operation = "Please select before or after";
    }
    
    // Validate task type selection
    if (!automationData?.targetValues) {
      newErrors.taskType = "Please select a task type";
    }
    
    // Cross-validation warnings
    if (eventType === 'task_completed' && config.operation === 'before' && config.amount > 0) {
      newErrors.warning = "Triggering before task completion may not work as expected";
    }
    
    logger.log('[TimeBasedTriggerBlock] Validation errors:', newErrors);
    setLocalErrors(newErrors);
    
    const isValid = Object.keys(newErrors).filter(key => key !== 'warning').length === 0;
    onValidationChange?.(isValid);
    
    return newErrors;
  }, [automationData?.targetValues, eventType, onValidationChange]);

  // Handle configuration changes
  const handleConfigChange = useCallback((newConfig: TimeOffsetConfig) => {
    logger.log('[TimeBasedTriggerBlock] Config changed:', newConfig);
    setIsDirty(true);
    onChange(newConfig);
    validateConfig(newConfig);
  }, [onChange, validateConfig]);

  // Validate on mount and when dependencies change
  useEffect(() => {
    if (timeOffsetConfig) {
      validateConfig(timeOffsetConfig);
    }
  }, [timeOffsetConfig, validateConfig]);

  // Helper functions
  const getTimingDescription = (eventType: string): string => {
    switch (eventType) {
      case 'task_due_date':
        return 'task due date';
      case 'task_completed':
        return 'task completion';
      default:
        return 'task timing';
    }
  };

  const getHelpText = () => {
    if (eventType === 'task_due_date') {
      return (
        <>
          <strong>Example:</strong> "2 days before due date" will trigger this automation 2 days before any selected task type is due.
          <br />
          <strong>Use case:</strong> Send reminder messages or create follow-up tasks before important deadlines.
        </>
      );
    }
    
    if (eventType === 'task_completed') {
      return (
        <>
          <strong>Example:</strong> "1 day after completion" will trigger this automation 1 day after any selected task type is marked complete.
          <br />
          <strong>Use case:</strong> Send follow-up surveys or create next steps after task completion.
        </>
      );
    }
    
    return null;
  };

  const generatePreviewText = useMemo(() => {
    if (!timeOffsetConfig.amount || !timeOffsetConfig.operation || !automationData?.targetValues) {
      return null;
    }
    
    const taskTypeName = automationData.targetValues === 'any' 
      ? 'any task'
      : taskData?.find(task => task.value === automationData.targetValues)?.name || 'selected task';
    
    return `Triggers ${timeOffsetConfig.amount} day${timeOffsetConfig.amount > 1 ? 's' : ''} ${timeOffsetConfig.operation} ${taskTypeName} ${getTimingDescription(eventType)}`;
  }, [timeOffsetConfig, automationData?.targetValues, taskData, eventType]);

  // Don't render if not visible
  if (!isVisible) {
    logger.log('[TimeBasedTriggerBlock] Not visible, not rendering');
    return null;
  }

  logger.log('[TimeBasedTriggerBlock] Rendering component');

  return (
    <div className="col-md-12 time-based-trigger-block">
      {/* Step indicator */}
      <span className="auto_dot auto_dot_time">
        <label className="dot_no">1.5</label>
      </span>
      
      {/* Header */}
      <label className="when_then_label">
        <Clock size={16} className="header-icon" />
        Time Configuration
      </label>
      <span className="when_then_trigger">
        Set when this automation should trigger relative to the task timing
      </span>

      {/* Task Type Selection Row */}
      <div className="condition-group">
        <div className="d-flex align-items-center gap-2">
          <span className="and">AND</span>
          <span className="and_source">Task type is</span>
          <select
            name="targetValues"
            className={`form-select and_select ${localErrors.taskType ? 'error' : ''}`}
            value={automationData?.targetValues || ''}
            onChange={onTaskTypeChange}
            disabled={isLoading}
          >
            <option value="">Select Task Type</option>
            {taskData?.map((task: any, i: number) => (
              <option value={task.value} key={i}>
                {task.name}
              </option>
            ))}
            <option value="any">Any Task Type</option>
          </select>
        </div>
        {localErrors.taskType && <ErrorText message={localErrors.taskType} />}
      </div>

      {/* Time Offset Configuration Row */}
      <div className="condition-group">
        <div className="d-flex align-items-center gap-2">
          <span className="and">AND</span>
          <span className="and_source">triggers</span>
          
          {/* Amount input */}
          <input
            type="number"
            min="1"
            max="365"
            value={timeOffsetConfig.amount || ''}
            onChange={(e) => handleConfigChange({
              ...timeOffsetConfig,
              amount: parseInt(e.target.value) || 1
            })}
            className={`form-control time-offset-input ${localErrors.amount ? 'error' : ''}`}
            disabled={isLoading}
            placeholder="Days"
          />
          
          <span className="time-unit">days</span>
          
          {/* Operation select */}
          <select
            value={timeOffsetConfig.operation}
            onChange={(e) => handleConfigChange({
              ...timeOffsetConfig,
              operation: e.target.value as 'before' | 'after'
            })}
            className={`form-select time-operation-select ${localErrors.operation ? 'error' : ''}`}
            disabled={isLoading}
          >
            <option value="before">before</option>
            <option value="after">after</option>
          </select>
          
          <span className="and_source">
            {getTimingDescription(eventType)}
          </span>
        </div>
        
        {/* Validation errors */}
        {localErrors.amount && <ErrorText message={localErrors.amount} />}
        {localErrors.operation && <ErrorText message={localErrors.operation} />}
      </div>

      {/* Preview text */}
      {generatePreviewText && (
        <div className="time-offset-preview">
          <Calendar size={14} className="preview-icon" />
          {generatePreviewText}
        </div>
      )}

      {/* Warning message */}
      {localErrors.warning && (
        <div className="time-offset-warning">
          <AlertCircle size={14} className="warning-icon" />
          {localErrors.warning}
        </div>
      )}

      {/* Help text */}
      <div className="time-offset-help">
        <div className="help-content">
          <Info size={14} className="help-icon" />
          <div className="help-text">
            {getHelpText()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TimeBasedTriggerBlock);